import type { RenDecorationFieldDefinition, RenOption } from '../../types'

export type DecorationEditorControl =
  | 'text'
  | 'textarea'
  | 'switch'
  | 'select'
  | 'segmented'
  | 'number'
  | 'slider'
  | 'color'
  | 'swatch'
  | 'image'
  | 'navigation'
  | 'list'

export type DecorationEditorField = RenDecorationFieldDefinition & {
  control: DecorationEditorControl
  min?: number
  max?: number
  step?: number
  options?: RenOption[]
}

const safeControls = new Set<DecorationEditorControl>([
  'text', 'textarea', 'switch', 'select', 'segmented', 'number', 'slider',
  'color', 'swatch', 'image', 'navigation', 'list'
])

export function parseDecorationFields(raw: string | undefined): DecorationEditorField[] {
  if (!raw?.trim()) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed.flatMap((entry) => {
    if (!isRecord(entry)) return []
    const code = text(entry.code)
    const label = text(entry.label)
    if (!code || !label || !/^[A-Za-z][A-Za-z0-9_.-]*$/.test(code)) return []
    const type = text(entry.type).toLowerCase() || 'string'
    const options = parseOptions(entry.options)
    const requestedControl = text(entry.control).toLowerCase() as DecorationEditorControl
    const control = safeControls.has(requestedControl)
      ? requestedControl
      : controlFor(type, options)
    return [{
      code,
      label,
      type,
      control,
      options,
      min: finiteNumber(entry.min),
      max: finiteNumber(entry.max),
      step: positiveNumber(entry.step)
    }]
  })
}

function controlFor(type: string, options: RenOption[]): DecorationEditorControl {
  if (options.length) return options.length <= 4 ? 'segmented' : 'select'
  if (type === 'boolean' || type === 'switch') return 'switch'
  if (type === 'number' || type === 'integer') return 'number'
  if (type === 'slider') return 'slider'
  if (type === 'color') return 'swatch'
  if (type === 'image' || type === 'image-url') return 'image'
  if (type === 'navigation' || type === 'link-target') return 'navigation'
  if (type === 'list' || type === 'array') return 'list'
  if (type === 'text' || type === 'textarea' || type === 'rich-text') return 'textarea'
  return 'text'
}

function parseOptions(value: unknown): RenOption[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    if (!isRecord(entry)) return []
    const label = text(entry.label)
    const optionValue = entry.value
    if (!label || !['string', 'number', 'boolean'].includes(typeof optionValue)) return []
    return [{ label, value: optionValue as string | number | boolean }]
  })
}

function finiteNumber(value: unknown): number | undefined {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function positiveNumber(value: unknown): number | undefined {
  const parsed = finiteNumber(value)
  return parsed !== undefined && parsed > 0 ? parsed : undefined
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}
